import { useEffect, useRef, useState } from "react";
import api, { BACKEND_URL, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Loader2, Download, FileSpreadsheet, FileImage, Globe, Copy, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const humanSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (contentType, filename) => {
  const ext = (filename || "").toLowerCase().split(".").pop();
  if (contentType?.includes("spreadsheet") || contentType?.includes("csv") || ["xls", "xlsx", "csv"].includes(ext)) return FileSpreadsheet;
  if (contentType?.startsWith("image/")) return FileImage;
  return FileText;
};

const authedHref = (path) => {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  return `${BACKEND_URL}/api/files/${path}`;
};

/**
 * Combined upload widget + list of documents.
 *
 * Props:
 *   listEndpoint   — GET, returns [{id, title, storage_path, ...}]
 *   uploadEndpoint — POST (multipart) with 'file' + optional 'title' + optional 'category'
 *   canEdit        — bool, controls whether the upload row + delete button render
 *   categories     — optional array of strings; when supplied the upload form shows a category select and the list groups by it
 *   emptyLabel     — copy shown when the list is empty
 *   allowPublic    — when true, admins can toggle library docs public and copy the shareable URL
 *   testIdPrefix   — data-testid prefix
 */
export default function DocumentPanel({ listEndpoint, uploadEndpoint, canEdit, categories, emptyLabel = "No documents yet.", allowPublic = false, testIdPrefix = "doc" }) {
  const inputRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(categories?.[0] || "");

  const load = async () => {
    try { const { data } = await api.get(listEndpoint); setDocs(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [listEndpoint]);

  const pick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Document must be under 20 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (title.trim()) fd.append("title", title.trim());
      if (categories && category) fd.append("category", category);
      await api.post(uploadEndpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setTitle("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeDoc = async (d) => {
    if (!window.confirm(`Remove "${d.title}"?`)) return;
    try { await api.delete(`/files/document/${d.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const togglePublic = async (d, next) => {
    try {
      await api.patch(`/files/document/${d.id}/public`, null, { params: { is_public: next } });
      toast.success(next ? "Now publicly visible" : "No longer public");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleAi = async (d, next) => {
    try {
      await api.patch(`/files/document/${d.id}/ai`, null, { params: { include_in_ai: next } });
      toast.success(next ? "Included in Ask Ernie" : "Excluded from Ask Ernie");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const publicUrl = () => `${window.location.origin}/public/documents`;
  const copyPublicUrl = async () => {
    try { await navigator.clipboard.writeText(publicUrl()); toast.success("Public link copied to clipboard"); }
    catch { toast.error("Copy failed — link: " + publicUrl()); }
  };

  const groups = categories
    ? [...categories, "Uncategorised"].map(cat => ({ cat, items: docs.filter(d => (d.category || "Uncategorised") === cat) })).filter(g => g.items.length > 0)
    : [{ cat: null, items: docs }];

  return (
    <div className="space-y-4" data-testid={testIdPrefix}>
      {canEdit && allowPublic && (
        <div className="card-tactical rounded-md p-4 flex flex-wrap gap-3 items-center border border-[#83B68B]/30">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1">Public handbook link</div>
            <div className="text-xs opacity-70">Share this URL anywhere — parents don't need to log in. Toggle "Public" on individual docs below to publish them.</div>
          </div>
          <code className="text-xs px-2 py-1 bg-muted rounded border border-border font-mono" data-testid={`${testIdPrefix}-public-url`}>/public/documents</code>
          <button onClick={copyPublicUrl} className="text-xs tracking-widest uppercase px-3 py-1.5 border border-[#83B68B]/40 hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5" data-testid={`${testIdPrefix}-copy-url`}>
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      )}
      {canEdit && (
        <div className="card-tactical rounded-md p-4">
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-3">Add a document</div>
          <div className="flex flex-wrap gap-3 items-center">
            <Input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Friendly title (optional)" className="flex-1 min-w-[220px]" data-testid={`${testIdPrefix}-title`} />
            {categories && (
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-44" data-testid={`${testIdPrefix}-category`}><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <button
              type="button"
              onClick={pick}
              disabled={busy}
              className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50"
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy ? "Uploading…" : "Upload"}
            </button>
            <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/jpeg,image/png" onChange={onFile} className="hidden" data-testid={`${testIdPrefix}-file-input`} />
          </div>
          <div className="text-[10px] opacity-50 mt-2">PDF, DOC/DOCX, XLS/XLSX, CSV, TXT, JPG or PNG · max 20 MB</div>
        </div>
      )}

      {loading ? (
        <div className="opacity-50 font-display tracking-widest text-sm">LOADING…</div>
      ) : docs.length === 0 ? (
        <div className="opacity-50 text-sm">{emptyLabel}</div>
      ) : (
        <div className="space-y-6">
          {groups.map(g => (
            <div key={g.cat || "all"}>
              {g.cat && <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-2">{g.cat}</div>}
              <div className="space-y-2">
                {g.items.map(d => {
                  const Icon = iconFor(d.content_type, d.original_filename);
                  return (
                    <div key={d.id} className="flex items-center gap-3 p-3 rounded border border-border bg-muted/40 hover:border-[#83B68B]/40 transition-colors" data-testid={`${testIdPrefix}-item-${d.id}`}>
                      <Icon className="w-5 h-5 text-[#83B68B] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <a href={authedHref(d.storage_path)} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:text-[#83B68B] truncate">{d.title}</a>
                          {d.is_public && <span className="text-[9px] tracking-widest uppercase bg-[#83B68B]/20 text-[#83B68B] px-1.5 py-0.5 rounded border border-[#83B68B]/30 inline-flex items-center gap-1"><Globe className="w-2.5 h-2.5" /> Public</span>}
                        </div>
                        <div className="text-[10px] opacity-50 mt-0.5 flex flex-wrap gap-x-2">
                          <span>{humanSize(d.size)}</span>
                          {d.uploaded_by_name && <span>· {d.uploaded_by_name}</span>}
                          {d.created_at && <span>· {format(parseISO(d.created_at), "d MMM yyyy")}</span>}
                        </div>
                      </div>
                      {canEdit && (d.include_in_ai !== undefined || d.has_extracted_text !== undefined) && (
                        <div
                          className="flex items-center gap-1.5"
                          title={
                            d.has_extracted_text === false
                              ? "No readable text — Ernie can't answer questions about this file (image or scanned PDF)"
                              : d.include_in_ai ? "Ask Ernie will use this doc's text when answering questions" : "Hidden from Ask Ernie"
                          }
                        >
                          <Sparkles className={`w-3.5 h-3.5 ${d.include_in_ai && d.has_extracted_text !== false ? "text-[#83B68B]" : "opacity-40"}`} />
                          <Switch
                            checked={!!d.include_in_ai && d.has_extracted_text !== false}
                            disabled={d.has_extracted_text === false}
                            onCheckedChange={(v)=>toggleAi(d, v)}
                            data-testid={`${testIdPrefix}-ai-toggle-${d.id}`}
                          />
                        </div>
                      )}
                      {canEdit && allowPublic && (
                        <div className="flex items-center gap-1.5" title={d.is_public ? "Public — visible without login" : "Private — logged-in members only"}>
                          <Globe className={`w-3.5 h-3.5 ${d.is_public ? "text-[#83B68B]" : "opacity-40"}`} />
                          <Switch checked={!!d.is_public} onCheckedChange={(v)=>togglePublic(d, v)} data-testid={`${testIdPrefix}-public-toggle-${d.id}`} />
                        </div>
                      )}
                      <a href={authedHref(d.storage_path)} target="_blank" rel="noopener noreferrer" className="text-xs tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5" data-testid={`${testIdPrefix}-download-${d.id}`}>
                        <Download className="w-3 h-3" /> Open
                      </a>
                      {canEdit && (
                        <button onClick={()=>removeDoc(d)} className="opacity-60 hover:opacity-100 hover:text-red-500 p-1.5" title="Remove" data-testid={`${testIdPrefix}-remove-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
